import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Loader2, Search, ArrowRight } from "lucide-react";
import {
  useGetConsultationsQuery,
  useLazyGetConsultationDetailQuery,
} from "@/services/consultationApi";
import { useLazyGetMemberByIdQuery } from "@/services/memberApi";
import { HistoryConsultation } from "@/types/consultation";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
import { toast } from "@/components/ui/use-toast";
import { formatDate } from "@/lib/helper";

interface HistoryFilterProps {
  memberId: number;
  onDataFound: (data: HistoryConsultation | null) => void;
  className?: string;
}

const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());

export const HistoryFilter: React.FC<HistoryFilterProps> = ({
  memberId,
  onDataFound,
  className,
}) => {
  const [month, setMonth] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 1;
  const { setValue } = useFormContext();

  const queryStr = memberId
    ? `member_id=${memberId}&month=${parseInt(month, 10)}&year=${parseInt(year, 10)}&status=approved&limit=100&sort_order=desc`
    : `month=${parseInt(month, 10)}&year=${parseInt(year, 10)}&status=approved&limit=100&sort_order=desc`;

  const { data: consultationsResponse, isFetching, isError } = useGetConsultationsQuery(
    { query: queryStr, orgId },
    { skip: !month || !year || !hasSearched }
  );

  const [triggerGetDetail, { isFetching: isDetailFetching }] = useLazyGetConsultationDetailQuery();
  const [triggerGetMember] = useLazyGetMemberByIdQuery();

  const handlePrefill = async (id: string | number) => {
    const consultationId = parseInt(String(id), 10);
    if (!consultationId || isNaN(consultationId)) {
      toast({ variant: "destructive", description: "Invalid consultation ID." });
      return;
    }

    try {
      const resp = await triggerGetDetail({ id: consultationId, org_id: orgId }).unwrap();

      // API returns { data: [ { consultation_details, question_answers, body_measurement, ... } ] }
      const raw = Array.isArray(resp?.data) ? resp.data[0] : Array.isArray(resp) ? resp[0] : resp;

      if (!raw) {
        toast({ variant: "destructive", description: "No data found for this consultation." });
        return;
      }

      const cd = raw.consultation_details || {};

      // ── 1. Consultation detail fields ─────────────────────────────────────
      if (cd.member_id)         setValue("member_id",         cd.member_id);
      if (cd.coach_id)          setValue("coach_id",          cd.coach_id);
      if (cd.height != null)    setValue("member_height",     cd.height);
      if (cd.weight != null)    setValue("member_weight",     cd.weight);

      // ── 2. Member info — fetch by id to populate name, dob, gender, email ─
      if (cd.member_id) {
        try {
          const memberResp = await triggerGetMember({ org_id: orgId, id: cd.member_id }).unwrap();
          if (memberResp) {
            const firstName = (memberResp.first_name || "").trim();
            const lastName  = (memberResp.last_name  || "").trim();
            setValue("member_name",   [firstName, lastName].filter(Boolean).join(" "));
            const dob = memberResp.dob instanceof Date
              ? memberResp.dob.toISOString()
              : memberResp.dob;
            setValue("member_dob",    String(dob || ""));
            setValue("member_gender", memberResp.gender || "");
            setValue("member_email",  memberResp.email  || "");
            if (cd.height == null && memberResp.height != null) setValue("member_height", memberResp.height);
            if (cd.weight == null && memberResp.weight != null) setValue("member_weight", memberResp.weight);
          }
        } catch {
          // non-critical — skip if member fetch fails
        }
      }

      // ── 3. Body measurements ──────────────────────────────────────────────
      if (raw.body_measurement && Object.keys(raw.body_measurement).length > 0) {
        setValue("body_measurement", raw.body_measurement);
      }

      // ── 4. Body analysis (body_test_analysis array → keyed object) ────────
      if (Array.isArray(raw.body_test_analysis) && raw.body_test_analysis.length > 0) {
        const latestByType: Record<string, any> = {};
        raw.body_test_analysis.forEach((item: any) => {
          const existing = latestByType[item.body_test_type];
          if (!existing || item.id > existing.id) latestByType[item.body_test_type] = item;
        });
        const bodyAnalysisObj: any = {};
        Object.values(latestByType).forEach((item: any) => {
          bodyAnalysisObj[item.body_test_type] = {
            age: item.age,
            rate: item.body_test_rate,
            rank: item.body_test_rank,
          };
        });
        setValue("body_analysis", bodyAnalysisObj);
      }

      // ── 5. Posture analysis ───────────────────────────────────────────────
      if (raw.posture_analysis && !Array.isArray(raw.posture_analysis)) {
        const { overhead_squat_anterior, overhead_squat_lateral, single_leg_anterior, pulling_pushing_lateral } = raw.posture_analysis;
        setValue("posture_analysis", { overhead_squat_anterior, overhead_squat_lateral, single_leg_anterior, pulling_pushing_lateral });
      }

      // ── 6. Goals ──────────────────────────────────────────────────────────
      if (Array.isArray(raw.goals) && raw.goals.length > 0) {
        setValue("goals", raw.goals);
        const allValues = raw.goals.flatMap((g: any) => g.goals ?? []);
        setValue("_prefilled_goal_values", allValues as any);
        setValue("_prefilled_goal_text", raw.goals[0]?.goal_text ?? "" as any);
      }

      // ── 7. Meal plan ──────────────────────────────────────────────────────
      if (Array.isArray(raw.meal_plans) && raw.meal_plans.length > 0) {
        const mealPlanIds = raw.meal_plans
          .map((p: any) => p.id)
          .filter((id: any) => id && !Number.isNaN(Number(id)));
        if (mealPlanIds.length > 0) {
          setValue("assigned_meal_plan_ids", mealPlanIds);
        }
      }

      // ── 8. Workout ────────────────────────────────────────────────────────
      if (Array.isArray(raw.workout_plans) && raw.workout_plans.length > 0) {
        const workoutIds = raw.workout_plans
          .map((p: any) => p.workout_id)
          .filter((id: any) => id && !Number.isNaN(Number(id)));
        if (workoutIds.length > 0) {
          setValue("assigned_workout_plan_ids", workoutIds);
        }
      }

      // ── 9. Q&A answers ────────────────────────────────────────────────────
      if (raw.question_answers && typeof raw.question_answers === "object") {
        const flatAnswers: Record<string, any> = {};
        Object.values(raw.question_answers).forEach((sectionAnswers: any) => {
          if (Array.isArray(sectionAnswers)) {
            sectionAnswers.forEach((ans: any) => {
              if (ans.question_id != null && ans.answer !== null) {
                flatAnswers[String(ans.question_id)] = ans.answer;
              }
            });
          }
        });
        setValue("answers", flatAnswers);
      }

      // ── 10. HistoryConsultation ───────────────────────────────────────────
      const historyFormatted: HistoryConsultation = {
        consultation_details: {
          id: cd.id ?? consultationId,
          member_id: cd.member_id ?? memberId,
          membership_id: cd.membership_id,
          coach_id: cd.coach_id,
          consultation_date: cd.consultation_date,
          status: cd.status,
          meal_plan_id: Array.isArray(cd.meal_plan_id) ? cd.meal_plan_id : cd.meal_plan_id ? [cd.meal_plan_id] : [],
          workout_plan_id: Array.isArray(cd.workout_plan_id) ? cd.workout_plan_id : cd.workout_plan_id ? [cd.workout_plan_id] : [],
        },
        question_answers: raw.question_answers ?? {},
        body_measurement: raw.body_measurement ?? {},
        body_test_analysis: raw.body_test_analysis ?? [],
        posture_analysis: raw.posture_analysis ?? {},
        goals: raw.goals ?? [],
        meal_plans: raw.meal_plans ?? [],
        workout_plans: raw.workout_plans ?? [],
      };

      onDataFound(historyFormatted);

      toast({ variant: "success", description: "Data prefilled from previous consultation." });
    } catch (err) {
      console.error("handlePrefill error:", err);
      toast({ variant: "destructive", description: "Error loading consultation data." });
    }
  };

  const selectedMonthLabel = months.find((m) => m.value === month)?.label;
  const consultationList = Array.isArray(consultationsResponse?.data)
    ? consultationsResponse.data
    : [];

  return (
    <Card className={cn("p-4 border-[#e2e8f0] bg-[#f8fafc]", className)}>
      <div className="flex flex-col space-y-3">
        {/* Header Section - Uper (Responsive) */}
        <div className="space-y-1">
          <p className="font-medium text-sm text-[#1e293b]">
            See previous consultation
          </p>
          <p className="text-xs text-[#64748b]">
            View and copy records from previous sessions
          </p>
        </div>

        {/* Button Section - Full Width Responsive Neeche */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full h-9 border-[#cbd5e1] hover:bg-white text-[#475569] font-medium justify-start"
              disabled={isFetching || isDetailFetching || !memberId}
            >
              <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
              <span className="truncate">
                {month && year 
                  ? `${selectedMonthLabel} ${year}` 
                  : "Select Period"
                }
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="end">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
                  Select Year
                </label>
                <Select value={year} onValueChange={(val) => { setYear(val); setHasSearched(false); }}>
                  <SelectTrigger className="h-9 focus:ring-0">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
                  Select Month
                </label>
                <Select value={month} onValueChange={(val) => { setMonth(val); setHasSearched(false); }}>
                  <SelectTrigger className="h-9 focus:ring-0" disabled={!year}>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="w-full h-9 bg-[#6be27f] hover:bg-[#49DE62] text-white font-medium" 
                onClick={() => {
                  setHasSearched(true);
                  setIsOpen(false);
                }}
                disabled={!month || !year || isFetching || isDetailFetching || !memberId}
              >
                <Search className="mr-2 h-4 w-4" />
                Find History
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Loading State */}
        {isFetching && (
          <div className="flex items-center justify-center py-2 text-[#64748b] text-xs font-medium">
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            Searching for consultation records...
          </div>
        )}

        {isDetailFetching && (
          <div className="flex items-center justify-center py-2 text-primary text-xs font-medium">
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            Fetching detail and prefilling form...
          </div>
        )}

        {/* Consultation List */}
        {hasSearched && !isFetching && consultationList.length > 0 && (
          <div className="space-y-2 mt-2 bg-white p-3 rounded-lg border border-[#e2e8f0]">
            <p className="text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wide">
              Select period to copy data:
            </p>
            <div className="max-h-36 overflow-y-auto space-y-1.5 custom-scrollbar">
              {consultationList.map((item: any) => {
                const consultationDetail = item?.consultation_details || item;
                const itemId = consultationDetail?.id;
                const itemDate = consultationDetail?.consultation_date;
                const itemStatus = consultationDetail?.status;
                
                if (!itemId) return null;
                
                return (
                  <div
                    key={itemId}
                    className="flex items-center justify-between p-2 rounded hover:bg-gray-50 border border-gray-100 text-xs transition-colors"
                  >
                    <div>
                      <span className="font-medium text-gray-700">
                        {formatDate(itemDate)}
                      </span>
                      <span className="ml-2 text-[10px] text-gray-400 capitalize">
                        ({itemStatus})
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      onClick={() => handlePrefill(itemId)}
                      className="h-7 px-2 text-primary hover:text-primary/80 text-[11px] font-semibold"
                    >
                      Copy Data <ArrowRight className="ml-1 w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No Records Found */}
        {hasSearched && !isFetching && !consultationList.length && (
          <p className="text-[11px] text-[#ef4444] font-medium italic mt-1 bg-red-50 p-1.5 rounded border border-red-100 flex items-center">
             No records found for {selectedMonthLabel} {year}
          </p>
        )}
      </div>
    </Card>
  );
};